import { useEffect, useRef, useState } from "react";

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

/**
 * Scroll reveal built on a native IntersectionObserver.
 *
 * This deliberately does not use framer-motion's `whileInView`: under the
 * smooth-scroll wrapper that viewport detection failed to fire, leaving whole
 * sections stuck at opacity 0. Everything here fails *visible* — if the
 * observer is unavailable, errors, or never fires, the content still shows.
 */
export const Reveal = ({
  children,
  delay = 0,
  y = 40,
  className = "",
  once = true,
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // No observer support, or reduced motion: show immediately.
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    // If it is already on screen at mount, don't wait for an intersection.
    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setShown(true);
      if (once) return;
    }

    let observer;
    try {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setShown(true);
              if (once && observer) observer.unobserve(entry.target);
            } else if (!once) {
              setShown(false);
            }
          });
        },
        { threshold: 0, rootMargin: "0px 0px -8% 0px" }
      );
      observer.observe(node);
    } catch {
      setShown(true);
      return;
    }

    // Safety net: never leave content hidden.
    const failsafe = setTimeout(() => setShown(true), 2000);

    return () => {
      clearTimeout(failsafe);
      if (observer) observer.disconnect();
    };
  }, [once]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity 0.9s ${EASE} ${delay}s, transform 0.9s ${EASE} ${delay}s`,
        willChange: shown ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

export default Reveal;
